/**
 * English message catalog.
 *
 * en is the source of truth for the key shape - id.ts is typed as
 * typeof en so a missing or misspelled key fails at compile time.
 */
export const en = {
    app: {
        name: "Flexv Coach",
    },
    nav: {
        group: {
            overview: "Overview",
            schedule: "Schedule & duties",
            profile: "My profile",
            merchant: "Merchant",
        },
        dashboard: { label: "Dashboard", subtitle: "Your performance at a glance" },
        assignments: { label: "Assignments", subtitle: "Table list with filters & actions" },
        affiliations: { label: "Affiliations", subtitle: "Outlets you are affiliated with" },
        sports: { label: "Sports", subtitle: "Sports you can coach" },
        plan: { label: "Coach Schedule", subtitle: "Weekly calendar view" },
        availability: { label: "Availability", subtitle: "Weekly availability rules" },
        contract: { label: "Contract", subtitle: "Contract and payroll details" },
        pricing: { label: "Service Pricing", subtitle: "Rates set for your sessions" },
        payroll: { label: "Payroll", subtitle: "Monthly estimated earnings" },
        invitations: { label: "Invitations", subtitle: "Club requests waiting for you" },
        profile: { label: "Profile", subtitle: "Your account details" },
        section: "Menus",
        search: "Search menu",
        clearSearch: "Clear search",
        noResults: "No menus found",
        collapse: "Collapse sidebar",
        expand: "Expand sidebar",
    },
    header: {
        openMenu: "Open menu",
        closeNavigation: "Close navigation",
        userMenu: "User menu",
        helpCenter: "Help Center",
        profile: "Profile",
        logout: "Logout",
    },
    theme: {
        label: "Theme",
        system: "System",
        light: "Light",
        dark: "Dark",
    },
    language: {
        label: "Language",
        id: "Bahasa Indonesia",
        en: "English",
    },
    coach: {
        freelance: "Freelance Coach",
        inHouse: "In-House Coach",
    },
    assignments: {
        title: "Assignments",
        subtitle: "Every session assigned to you, with quick status actions.",
        loading: "Loading assignments...",
        loadFailed: "Assignments could not be loaded. Please try again.",
        actionFailed: "The status could not be updated. Please try again.",
        unnamed: "Session",
        preset: { week: "This week", month: "Next 30 days" },
        filter: {
            title: "Filters",
            range: "Date range",
            from: "From",
            to: "To",
            outlet: "Outlet",
            status: "Status",
            allOutlets: "All outlets",
            allStatuses: "All statuses",
            upcomingOnly: "Upcoming only",
            reset: "Reset",
            active: "Active",
        },
        listTitle: "Assignment list",
        listCount: "{count} session(s)",
        column: {
            date: "Date & time",
            session: "Session",
            outlet: "Outlet",
            status: "Status",
            actions: "Actions",
        },
        action: {
            confirmed: "Confirm",
            completed: "Complete",
            cancelled: "Cancel",
        },
        empty: {
            title: "No assignments found",
            description: "Try widening the date range or clearing the filters.",
        },
    },
    affiliations: {
        title: "Affiliations",
        subtitle: "Outlets and companies where you are registered as a coach.",
        loading: "Loading affiliations...",
        loadFailed: "Affiliations could not be loaded. Please try again.",
        readOnlyNote:
            "Affiliations are managed by the merchant. Requests from both sides are coming soon.",
        primary: "Primary",
        type: "Employment type",
        scope: "Scope",
        scopeOutlet: "Outlet",
        scopeCompany: "Company-wide",
        companyWide: "Company-wide",
        verifiedNote: "Active affiliation — you can be scheduled at this outlet.",
        status: {
            active: "Active",
            pending_approval: "Pending approval",
            rejected: "Rejected",
            inactive: "Inactive",
            terminated: "Terminated",
        },
        empty: {
            title: "No affiliations yet",
            description: "Once a merchant affiliates you, it will appear here.",
        },
    },
    availability: {
        title: "Availability",
        subtitle: "Weekly windows when you can be scheduled to coach.",
        loading: "Loading availability...",
        loadFailed: "Availability could not be loaded. Please try again.",
        readOnlyNote: "Availability is managed by your outlet admin — contact them if anything needs changing.",
        summary: {
            totalHours: "Total hours",
            hoursPerWeek: "{h} hrs/week",
            daysActive: "{n} active days",
            windows: "{n} windows",
        },
        effectiveFrom: "Effective from {date}",
        effectiveUntil: "until {date}",
        unavailable: "Unavailable",
        closed: "Closed",
        empty: {
            title: "No availability set",
            description: "Ask your outlet admin to configure your weekly schedule.",
        },
    },
    sports: {
        title: "Sports",
        subtitle: "Manage the sports you coach and pick your main one.",
        loading: "Loading sports...",
        loadFailed: "Sports could not be loaded. Please try again.",
        actionFailed: "The change could not be saved. Please try again.",
        primary: "Primary",
        add: {
            title: "Add a sport",
            subtitle: "Pick from the sports available in the Flexv catalogue.",
            sport: "Sport",
            owned: "Already in your profile",
            placeholder: "Select a sport",
            submit: "Add sport",
            hint: "Pick one sport from the catalogue, then press Add.",
            ready: "ready to add",
            emptyMaster: "All available sports are already in your profile.",
            },
            picker: {
                title: "Select a sport",
                subtitle: "All sports from the Flexv master list",
                search: "Search sport...",
                noResult: "No sport matches your search.",
                open: "Tap to browse all sports",
            },
            list: {
            title: "Your sports",
            added: "Shown on your coach profile",
        },
        action: {
            setPrimary: "Set primary",
            unsetPrimary: "Unset primary",
            remove: "Remove",
        },
        empty: {
            title: "No sports yet",
            description: "Add the sports you coach so merchants can match you.",
        },
    },
    plan: {
        title: "Coach schedule",
        subtitle: "Your weekly teaching schedule",
        today: "Today",
        thisWeek: "This week",
        prevWeek: "Previous week",
        nextWeek: "Next week",
        loading: "Loading your schedule...",
        loadFailed: "Your schedule could not be loaded. Please try again.",
        actionFailed: "The status could not be updated. Please try again.",
        day: {
            off: "Off",
            empty: "No sessions scheduled.",
            sessionCount: "{count} session(s)",
        },
        summary: {
            sessions: "Sessions this week",
            sessionsHint: "{count} as lead coach",
            totalHours: "Total hours",
            totalHoursHint: "Sum of all assigned windows",
            completed: "Completed",
            completedHint: "{count} still scheduled",
            busiestDay: "Busiest day",
            busiestHint: "No sessions in range",
        },
        filter: {
            title: "Filters",
            outlet: "Outlet",
            status: "Status",
            allOutlets: "All outlets",
            allStatuses: "All statuses",
            reset: "Reset",
        },
        empty: {
            title: "No sessions this week",
            description: "Try another week or clear the filters.",
        },
    },
    dashboard: {
        greeting: "Welcome back, {name}",
        subtitle: "Your coaching performance",
        loading: "Loading your dashboard...",
        loadFailed: "Dashboard could not be loaded. Please try again.",
        unspecified: "Unspecified",
        range: {
            label: "Dashboard range",
            week: "This week",
            month: "This month",
        },
        stats: {
            sessions: "Sessions",
            teachingHours: "Teaching hours",
            completionRate: "Completion rate",
            completionHint: "Completed vs decided sessions",
            rating: "Rating",
            ratingHint: "Average member rating",
            vsPrevious: "vs previous period",
            noPrevious: "No data in previous period",
        },
        charts: {
            trend: {
                title: "Sessions per day",
                subtitle: "Daily workload for the selected range",
            },
            attendance: {
                title: "Attendance breakdown",
                subtitle: "Status of this period's sessions",
            },
            outlets: { title: "Sessions by outlet" },
            sports: { title: "Sessions by sport" },
        },
        payroll: {
            title: "Payroll estimate",
            subtitle: "Based on your active contract",
            disclaimer: "Estimate only — final payroll is processed by the merchant.",
            basis: "Payroll basis",
            eligibleSessions: "Eligible sessions",
            eligibleHours: "Eligible hours",
            note: {
                no_contract: "No active contract yet, so payroll cannot be estimated.",
                rate_missing: "Your contract has no rate yet, so payroll cannot be estimated.",
                basis_unknown: "Payroll basis is not recognized, so payroll cannot be estimated.",
            },
        },
        upcoming: {
            title: "Upcoming sessions",
            subtitle: "Your next scheduled duties",
            empty: "No upcoming sessions",
            emptyDescription: "New assignments from the merchant will show up here.",
            viewAll: "View all",
        },
        empty: {
            title: "No data yet",
            description: "Statistics will appear once you have sessions in this period.",
        },
    },
    profile: {
        about: "About",
        contact: "Contact",
        outlets: "Outlets",
        sports: "Sports",
        email: "Email",
        phone: "Phone",
        joined: "Joined",
        primary: "Primary",
        rating: "Rating",
        noAffiliations: "No active affiliations.",
        unverifiedEmail: "Email not verified. Some features may be limited.",
        verifyNow: "Verify now",
        subtitle: "Who you are as a coach — visible across your outlets.",
        edit: {
            open: "Edit profile",
            title: "Edit profile",
            subtitle: "Only these three can be changed. Email & login name stay fixed.",
            displayName: "Display name",
            displayNamePlaceholder: "e.g. Coach Andi",
            phone: "Phone",
            phonePlaceholder: "+62 ...",
            bio: "Bio",
            bioPlaceholder: "Short intro clients see...",
            submit: "Save changes",
            cancel: "Cancel",
            success: "Profile updated.",
            phoneTaken: "This phone number is already in use by another account.",
            failed: "Profile could not be saved. Please try again.",
        },
        stats: {
            sessions: "Sessions",
            hours: "Teaching hours",
            outlets: "Outlets",
            rating: "Rating",
        },
    },
    invitations: {
        title: "Club Invitations",
        subtitle: "Clubs that asked you to join. Only you can decide.",
        loading: "Loading invitations...",
        loadFailed: "Invitations could not be loaded. Please try again.",
        decideFailed: "That decision could not be saved. Please try again.",
        decidedByYou:
            "These requests were submitted by the club, and they are waiting for YOUR approval — the merchant cannot approve them on your behalf.",
        companyWide: "Company-wide (no specific outlet)",
        employmentType: "Employment",
        requestedAt: "Requested",
        awaitingYou: "Waiting for you",
        firstClubNote:
            "The first club you accept becomes your home club; later clubs stay secondary.",
        accept: "Accept",
        decline: "Decline",
        processing: "Saving...",
        empty: {
            title: "No invitations",
            description:
                "When a club invites you to join, the request shows up here for your approval.",
        },
    },
    pricing: {
        title: "Service Pricing",
        subtitle: "Rates set for your coaching sessions at each outlet.",
        loading: "Loading pricing...",
        loadFailed: "Pricing could not be loaded. Please try again.",
        readOnlyNote: "Prices are set by the merchant. Contact them to update your rates.",
        stats: {
            outlets: "Outlets",
            activePrices: "Active rates",
            serviceTypes: "Service types",
        },
        column: {
            outlet: "Outlet",
            sport: "Sport",
            serviceType: "Service type",
            basis: "Basis",
            price: "Price",
            status: "Status",
            effective: "Effective period",
        },
        basis: {
            flat_per_booking: "Flat per booking",
            hourly: "Hourly",
        },
        serviceType: {
            venue_booking: "Venue Booking",
            venue_booking_add_on: "Venue Booking Add-on",
            membership: "Membership",
        },
        status: {
            active: "Active",
            inactive: "Inactive",
        },
        empty: {
            title: "No pricing set yet",
            description: "Once a merchant sets your rates, they will appear here.",
        },
    },
    payroll: {
        title: "Payroll",
        subtitle: "Monthly estimate of your coaching earnings.",
        loading: "Loading payroll...",
        loadFailed: "Payroll could not be loaded. Please try again.",
        disclaimer: "Estimate only — final payslips are issued by your merchant.",
        stats: {
            total: "Estimated total",
            sessions: "Eligible sessions",
            hours: "Teaching hours",
        },
        month: {
            label: "Month",
            prev: "Previous month",
            next: "Next month",
        },
        tableTitle: "Breakdown by outlet",
        column: {
            outlet: "Outlet",
            sessions: "Sessions",
            hours: "Hours",
            rate: "Rate",
            basis: "Basis",
            estimate: "Estimate",
            note: "Note",
        },
        noDaily: "No pay-per-session data for this month.",
        basis: {
            flat_per_session: "Flat per session",
            hourly: "Hourly",
            monthly: "Monthly",
            per_pax: "Per participant",
            revenue_share: "Revenue share",
        },
        notes: {
            no_contract: "No active contract for this outlet this month.",
            rate_missing: "Contract has no rate for its basis.",
            basis_unknown: "Basis (per participant / revenue share) can't be calculated here.",
        },
        empty: {
            title: "No sessions this month",
            description: "Assignments with confirmed payment will appear here.",
        },
    },
    auth: {
        subtitle: "Sign in to see your coaching schedule",
        heroEyebrow: "Built for coaches",
        heroTitleA: "Make Coaching Career",
        heroTitleB: "Easier Together.",
        heroDesc:
            "Join Flexv, set your own schedule, pick the sessions you want, and build your name as a go-to coach.",
        heroCta: "No account yet?",
        heroCtaButton: "Sign Up Now",
        identifier: "Email or Username",
        password: "Password",
        signIn: "Sign In",
        demoAccount: "Demo account",
        signInFailed: "Unable to sign in. Please try again.",
        invalidCredentials: "Wrong email/username/phone or password. Please check and try again.",
        accountInactive: "This account is not active. Please contact support.",
        sessionExpired: "Session expired. Please sign in again.",
        googleInvalid: "Google session expired. Please try Google sign-in again.",
        googleDifferent: "This account is already linked to a different Google account.",
        networkFailed: "Network error. Check your connection and try again.",
        checkEmailCode: "Your account is not verified yet. We sent a new code to {email}.",
        or: "or",
        googleSignIn: "Continue with Google",
        googleSignInFailed: "Google sign-in could not be completed. Please try again.",
        restoring: "Restoring your session...",
        noAccount: "New coach?",
        createAccount: "Create an account",
        signup: {
            subtitle: "Complete your profile to create a coach account",
            fullName: "Full Name",
            fullNamePlaceholder: "Name as on your ID",
            phone: "Phone Number",
            phonePlaceholder: "0812xxxxxxx",
            email: "Email",
            emailPlaceholder: "coach@flexv.my.id",
            password: "Password",
            passwordHint: "Min. 8 characters, max. 128 characters",
            confirmPassword: "Confirm Password",
            submit: "Create Account",
            haveAccount: "Already have an account?",
            backToSignIn: "Sign in",
            failed: "Sign up could not be completed. Please try again.",
            fullNameInvalid: "Enter your full name (min. 2 characters).",
            phoneInvalid: "Enter a valid phone number (7–14 digits after country code).",
            emailInvalid: "Enter a valid email address.",
            passwordTooShort: "Password must be at least 8 characters.",
            passwordMismatch: "Password confirmation does not match.",
            emailTaken: "This email is already registered. Try signing in instead.",
            phoneTaken: "This phone number is already registered. Try signing in instead.",
            accountTaken: "This email or phone number is already registered.",
            googleInvalid: "Google session expired. Please go back and tap Continue with Google again.",
            googleMismatch: "Google account email does not match the email you typed.",
            googleLinkedOther: "This Google account is already linked to another coach.",
            googleLinkedNote: "We'll link this Google account once you finish signing up.",
            otpSent: "Account created! We sent a 6-digit code to {email}.",
        },
        verify: {
            title: "Verify your email",
            subtitle: "Enter the 6-digit code we sent to your email.",
            email: "Email",
            code: "Verification code",
            codeHint: "6 digits, valid for 10 minutes",
            emailRequired: "Enter your email address.",
            codeInvalid: "Enter the 6-digit code.",
            submit: "Verify",
            resend: "Resend code",
            resendCooldown: "Resend in {s}s",
            resent: "A new code was sent to your email.",
            backToLogin: "Back to login",
            failed: "Verification could not be completed. Please try again.",
            invalidCode: "Wrong code. Check the latest email and try again.",
            expired: "Code expired. Tap Resend code for a new one.",
            locked: "Too many wrong attempts. Wait 15 minutes or tap Resend for a new code.",
            tooFast: "Please wait {s}s before requesting a new code.",
            noAccount: "No account found for this email. Please sign up first.",
            alreadyVerified: "Email already verified. Please sign in to continue.",
            sessionLost: "Email verified! Session expired — please sign in again.",
            verifiedOk: "Email verified! Signing you in…",
            sendFailed: "Failed to send the email. Check the address or try Resend.",
        },
    },
};

export type Messages = typeof en;
